import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const MANAGE_TABS = [
  { label: '고객', path: '/realtor/manage' },
  { label: '추천', path: '/realtor/manage/referral' },
  { label: '정산', path: '/realtor/manage/settlement' },
];

const FILTER_CHIPS = [
  { label: '발송', value: 'sent' },
  { label: '가입', value: 'joined' },
  { label: '만료예정', value: 'expiring' },
  { label: '만료', value: 'expired' },
];

const LINKS = [
  { label: '추천 가입회원 조회', path: '/realtor/manage/referral-members' },
  { label: '추천수익 리더보드', path: null },
];

// ───── 스타일 ─────
const Wrap = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const HeaderArea = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.base};
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const HeaderDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.base};
`;

const EarningsCard = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  text-align: left;
`;

const EarningsLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
`;

const EarningsAmount = styled.p`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: #fff;
  margin: 2px 0;
`;

const EarningsSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.lg} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
`;

const TabItem = styled.button`
  flex: 1;
  padding: 12px 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: -1px;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.xs};
`;

const SectionDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const FilterRow = styled.div`
  display: flex;
  background: #F3F4F6;
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterChip = styled.button`
  flex: 1;
  padding: 8px 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
`;

const LinkList = styled.div`
  margin: ${({ theme }) => theme.spacing.base} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
  border-top: 6px solid ${({ theme }) => theme.colors.borderLight};
`;

const LinkItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% + ${({ theme }) => theme.realtor.pagePadding} * 2);
  margin-left: -${({ theme }) => theme.realtor.pagePadding};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.realtor.pagePadding};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: none;
  cursor: pointer;
  text-align: left;
`;

const LinkLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text};
`;

const LinkArrow = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// ───── 컴포넌트 ─────
const ReferralStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeChip, setActiveChip] = useState('sent');

  return (
    <Wrap>
      <HeaderArea>
        <HeaderTitle>관리·정산</HeaderTitle>
        <HeaderDesc>매출과 고객 흐름을 한 눈에 확인하세요</HeaderDesc>
        <EarningsCard>
          <EarningsLabel>출금 가능 수익</EarningsLabel>
          <EarningsAmount>0원</EarningsAmount>
          <EarningsSub>총 수수료 0원</EarningsSub>
        </EarningsCard>
      </HeaderArea>

      <TabRow>
        {MANAGE_TABS.map((t) => (
          <TabItem
            key={t.path}
            $active={location.pathname === t.path}
            onClick={() => navigate(t.path)}
          >
            {t.label}
          </TabItem>
        ))}
      </TabRow>

      <SectionTitle>공인중개사 추천 현황</SectionTitle>
      <SectionDesc>추천받아 가입 전환까지의 성과를 추적합니다.</SectionDesc>

      <FilterRow>
        {FILTER_CHIPS.map((c) => (
          <FilterChip
            key={c.value}
            $active={activeChip === c.value}
            onClick={() => setActiveChip(c.value)}
          >
            {c.label}
          </FilterChip>
        ))}
      </FilterRow>

      <LinkList>
        {LINKS.map((l) => (
          <LinkItem
            key={l.label}
            onClick={() => l.path ? navigate(l.path) : alert('준비 중입니다.')}
          >
            <LinkLabel>{l.label}</LinkLabel>
            <LinkArrow>&gt;</LinkArrow>
          </LinkItem>
        ))}
      </LinkList>
    </Wrap>
  );
};

export default ReferralStatus;
